import type { PassengerResponse, SeatType, TripType } from '../api/models/bff';
import type { PassengerSeatingInput } from '../api/models/TripSearch';

const DEFAULT_SEAT_TYPE: SeatType = 'LAP';

/**
 * The seat types the API offers for a passenger. Empty means the passenger has no seat choice,
 * and therefore no seat type to send.
 */
export function getSeatOptions(passenger: PassengerResponse | undefined): SeatType[] {
  const seatTypes = passenger?.flightOptions?.flatMap(({ seatType }) => (seatType ? [seatType] : [])) ?? [];
  return [...new Set(seatTypes)];
}

/**
 * The credits the passenger consumes for a trip type and seat type, if the API provides them.
 * An option for the exact trip type wins over one that applies to any trip type.
 */
export function getPassengerCredits(passenger: PassengerResponse, tripType?: TripType, seatType?: SeatType): number | undefined {
  const matchingOptions = passenger.flightOptions?.filter(option => option.seatType === seatType) ?? [];
  const option = matchingOptions.find(candidate => candidate.tripType === tripType)
    ?? matchingOptions.find(candidate => !candidate.tripType);
  return option?.credits;
}

/** The seat type applied until the user picks another: LAP when offered, else the only/first offered option. */
export function getDefaultSeatOption(seatOptions: SeatType[]): SeatType | undefined {
  return seatOptions.includes(DEFAULT_SEAT_TYPE) ? DEFAULT_SEAT_TYPE : seatOptions[0];
}

/**
 * Resolves the seat type of each selected passenger. A passenger only carries one when the API
 * offers flight options for it: the explicit choice wins, and LAP is the default when offered
 * (else the API's first offered option).
 *
 * @param passengers - The selectable passengers, as returned by the API
 * @param selectedIds - Ids of the passengers to travel, in the order they must be sent
 * @param seatTypes - Explicit per-passenger seat choices, keyed by person id
 */
export function resolvePassengerSeating(
  passengers: PassengerResponse[],
  selectedIds: string[],
  seatTypes: Record<string, SeatType> = {},
): PassengerSeatingInput[] {
  return selectedIds.map((id) => {
    const seatOptions = getSeatOptions(passengers.find(passenger => passenger.person.id === id));
    if (!seatOptions.length) return { id };

    const chosenSeatType = seatTypes[id];
    return { id, seatType: chosenSeatType && seatOptions.includes(chosenSeatType) ? chosenSeatType : getDefaultSeatOption(seatOptions)! };
  });
}
